"use client";

import type { ProductCard, ProductDetails } from "@raumagarn/formatters";
import { type Selection, useAddToCart } from "@trydig/centra/client";
import { clsx } from "@trydig/ui";
import { Suspense } from "react";
import { AddToCartIcon } from "~/components/icons/32/AddToCartIcon";
import { NotifyMeModal } from "~/components/ui/AddToCartButton/NotifyMe/NotifyMeModal";
import { Counter } from "~/components/ui/Counter";
import { Skeleton } from "~/components/ui/Skeleton";
import { useQuantityInput } from "~/components/ui/VariantPicker/hooks/useQuantityInput";
import { gtm } from "~/lib/gtm";
import { selectionItemToGTMProduct } from "~/lib/gtm/utils";
import { useCart } from "~/lib/hooks/useCart";
import { useModal } from "~/lib/hooks/useModal";
import { usePrice } from "~/lib/hooks/usePrice";
import { formatPrice } from "~/lib/utils/price";
import styles from "./AddToCartButton.module.css";

export interface AddToCartButtonProps {
	selectedSize?: NonNullable<ProductDetails["variants"]>[number]["size"];

	quantityPicker?: boolean;
	product: ProductDetails | ProductCard;
}

const UnsuspendedAddToCartButton = ({
	selectedSize,
	quantityPicker = false,
	product,
}: AddToCartButtonProps) => {
	const size = selectedSize;
	const [quantityInput] = useQuantityInput(1, [1], 1);
	const { modal } = useCart();
	const notifyMeModal = useModal("notify", () => {}, true);
	const { showAsOnSale, price, priceBeforeDiscount } = usePrice({
		pricelist: product.pricelist,
	});

	const { mutate: addToCart } = useAddToCart();

	const noStock = !size?.stock || (typeof size.stock === "string" && size.stock === "no");

	if (quantityPicker) {
		return (
			<div className={styles.wrapper}>
				<Counter {...quantityInput} large disabled={noStock} />
				<button
					type="button"
					title={noStock ? "Ikke på lager" : undefined}
					className={clsx(styles["add-to-cart"])}
					onClick={() => {
						if (noStock) {
							notifyMeModal.openModal();
							return;
						}

						if (size.item)
							addToCart(
								{
									itemId: size.item,
								},
								{
									onSuccess: (selection: Selection, { itemId }) => {
										const selectionItem = selection?.selection?.items?.find(
											(item) => item.item === itemId,
										);

										if (selectionItem && selection?.selection?.currency) {
											gtm.addToCart(
												selectionItemToGTMProduct({
													item: selectionItem,
													currency: selection.selection.currency,
												}),
											);
										}
									},
								},
							);
						modal.openModal();
					}}
				>
					<span className={styles.large}>
						{!noStock ? "Legg i handlekurv" : "Varsle meg når varen er på lager"}
					</span>
					<span className={styles.mobile}>
						<AddToCartIcon />
					</span>
					{!noStock && (
						<span>
							{formatPrice(price)}
							{showAsOnSale && (
								<span className={styles.discountedPrice}>{priceBeforeDiscount}</span>
							)}
						</span>
					)}
				</button>
			</div>
		);
	}
	return (
		<button
			type="button"
			title={noStock ? "Ikke på lager" : undefined}
			className={clsx(styles["add-to-cart"])}
			onClick={() => {
				if (noStock) {
					notifyMeModal.openModal();
					return;
				}

				if (size.item)
					addToCart(
						{
							itemId: size.item,
						},
						{
							onSuccess: (selection: Selection, { itemId }) => {
								const selectionItem = selection?.selection?.items?.find(
									(item) => item.item === itemId,
								);
								if (selectionItem && selection?.selection?.currency) {
									gtm.addToCart(
										selectionItemToGTMProduct({
											item: selectionItem,
											currency: selection.selection.currency,
										}),
									);
								}
							},
						},
					);
				modal.openModal();
			}}
		>
			{!noStock ? "Legg i handlekurv" : "Varsle meg når varen er på lager"}
			<span>
				{formatPrice(price)}{" "}
				{showAsOnSale && (
					<span className={styles.discountedPrice}>{formatPrice(priceBeforeDiscount)}</span>
				)}{" "}
			</span>
		</button>
	);
};

export const AddToCartButton: typeof UnsuspendedAddToCartButton = (props) => {
	return (
		<Suspense fallback={<Skeleton height="3.5rem" />}>
			<UnsuspendedAddToCartButton {...props} />
			<NotifyMeModal {...props} />
		</Suspense>
	);
};
